/**
 * Los límites de un plan, y cómo se dice "sin límite".
 *
 * 🔴 Por qué hace falta un centinela y no `null`.
 *
 * Las columnas de límites (`maxUsers`, `maxQuotesPerMonth`…) son enteros que no admiten
 * nulo, así que "ilimitado" no se puede guardar como tal. Se guarda un número muy alto.
 *
 * El problema es que un número muy alto **se imprime**. La pantalla de planes mostraba
 * literalmente "999999 cotizaciones/mes", y la barra lateral del cliente mostraba
 * "24/999999" con una barra de progreso vacía. Eso no se lee como *ilimitado*: se lee
 * como un error del sistema. Peor todavía en la barra lateral, porque esa la ve el
 * cliente, no la plataforma.
 *
 * Así que el centinela vive en un solo lugar, con nombre, y las pantallas preguntan en
 * vez de imprimir. Si algún día se implementan las cuotas de verdad, el backend tiene
 * que consultar esto mismo y tratarlo como "no hay tope" — no comparar contra 999999
 * como si fuera una cantidad.
 */
export const SIN_LIMITE = 999_999;

/**
 * Si este límite significa "sin tope".
 *
 * Se compara con `>=` y no con `===` a propósito: si alguien carga 1.000.000 a mano
 * desde la pantalla de planes, la intención es obviamente la misma y no tiene sentido
 * que la interfaz lo trate como una cantidad exacta.
 */
export function esSinLimite(max: number | null | undefined): boolean {
  return max === null || max === undefined || max >= SIN_LIMITE;
}

/**
 * El límite en palabras, con el sustantivo que le corresponde.
 *
 * El género va explícito por campo porque en español no se puede derivar: son
 * "cotizaciones ilimitadas" y "modelos ilimitados". Una plantilla con "ilimitado/a"
 * armada al vuelo termina escribiendo mal la mitad de las etiquetas.
 */
export function formatearLimite(max: number, singular: string, plural: string): string {
  if (esSinLimite(max)) return plural;
  return `${max.toLocaleString('es-BO')} ${singular}`;
}
